import { Localized } from "@fluent/react";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material"
import { useCallback, useState } from "react";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { useAppDispatch } from "../../../app/hooks";
import { actionUserRequest, createUserRequest, deleteUserRequest, updateUserRequest, UserRequests } from "../userRequestsApi";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { If } from "../../../app/If";

type AddEditUserRequestDialogMode = 'create' | 'edit' | 'read' | 'adminEdit' | 'delete'

export interface AddEditUserRequestDialogProps {
    userRequest: UserRequests | undefined
    mode: AddEditUserRequestDialogMode;
    onSuccessfullySaved: (update: UserRequests | number) => void;
    onCancel: () => void
}

export const AddEditUserRequestDialog: React.FC<AddEditUserRequestDialogProps> = ({ userRequest, mode, onSuccessfullySaved, onCancel }) => {
    const dispatch = useAppDispatch()
    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [message, setMessage] = useState<string>(userRequest?.message ?? "");
    const [actionMessage, setActionMessage] = useState<string>(userRequest?.actionMessage ?? "");
    const [status, setStatus] = useState<"NEW" | "CLOSED" | "REJECTED">(userRequest?.status ?? "NEW");
    const [password, setPassword] = useState<string>("");

    const handleMessageChange = useCallback(e => {
        setMessage(e.target.value)
    }, [])

    const handleActionMessageChange = useCallback(e => {
        setActionMessage(e.target.value)
    }, [])

    const handleStatusChange = (e) => setStatus(e.target.value);

    const handlePasswordChange = useCallback(e => {
        setPassword(e.target.value)
    }, [])

    const handleSaveClick = async () => {
        setSaving(true)

        try {
            switch (mode) {
                case "create":  
                    const result = await dispatch(createUserRequest({ id: 0, message, messageType: "PERSONAL_DATA" }))
                    if (createUserRequest.fulfilled.match(result)) {
                        dispatch(showSuccess("saved"))
                        onSuccessfullySaved(result.payload as UserRequests)
                    } else {
                        let errorMsg = "error"
                        if (result.payload && result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title

                            }
                        }
                        setServerError(errorMsg)
                    }
                    break
                case "edit":
                    if(userRequest !== undefined) {
                        const result = await dispatch(updateUserRequest({ id: userRequest.id, message, messageType: "PERSONAL_DATA" }))
                        if (updateUserRequest.fulfilled.match(result)) {
                            dispatch(showSuccess("saved"))
                            onSuccessfullySaved(result.payload as UserRequests)
                        } else {
                            let errorMsg = "error"
                            if (result.payload && result.payload.kind === 'http') {
                                const problem = result.payload.problem
                                if (problem) {
                                    errorMsg = problem.title

                                }
                            }
                            setServerError(errorMsg)
                        }
                    }
                    break
                case "adminEdit":
                    if(userRequest !== undefined) {
                        const result = await dispatch(actionUserRequest({ id: userRequest.id, actionMessage, status: status as "CLOSED" | "REJECTED", password }))
                        if (actionUserRequest.fulfilled.match(result)) {
                            dispatch(showSuccess("saved"))
                            onSuccessfullySaved(result.payload as UserRequests)
                        } else {
                            let errorMsg = "error"
                            if (result.payload && result.payload.kind === 'http') {
                                const problem = result.payload.problem
                                if (problem) {
                                    errorMsg = problem.title

                                }
                            }
                            setServerError(errorMsg)
                        }
                    }
                    break
                case "delete":
                    if(userRequest !== undefined) {
                        const result = await dispatch(deleteUserRequest({ id: userRequest.id, password }))
                        if (deleteUserRequest.fulfilled.match(result)) {
                            dispatch(showSuccess("saved"))
                            onSuccessfullySaved(result.payload as number)
                        } else {
                            let errorMsg = "error"
                            if (result.payload && result.payload.kind === 'http') {
                                const problem = result.payload.problem
                                if (problem) {
                                    errorMsg = problem.title

                                }
                            }
                            setServerError(errorMsg)
                        }
                    }
                    break;
            }

        }
        catch (error) {
            dispatch(showError("error"));
        }
        finally {
            setSaving(false)
        }
    }

    return <>
        <Dialog open={true} maxWidth="md" fullWidth>
            <DialogTitle>
                <If condition={mode === "create"}><Localized id="user-requests-create">_Utwórz zgłoszenie</Localized></If>
                <If condition={mode === "edit" || mode === "adminEdit"}><Localized id="user-requests-edit">_Edytuj zgłoszenie</Localized></If>
                <If condition={mode === "read"}><Localized id="user-requests-details">_Szczegóły zgłoszenia</Localized></If>
                <If condition={mode === "delete"}><Localized id="user-requests-delete">_Usuń zgłoszenie</Localized></If>
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={2} padding={2}>
                    <TextField fullWidth value={message} onChange={handleMessageChange} rows={4} multiline type={"text"} label={<Localized id="user-requests-message">_Wiadomość</Localized>} disabled={mode === "read" || mode === "adminEdit" || mode === "delete"} />
                    
                    <If condition={mode === 'adminEdit' || mode === 'read'}>
                        <TextField fullWidth value={actionMessage} onChange={handleActionMessageChange} rows={4} multiline type={"text"} label={<Localized id="user-requests-action-message">_Wiadomość administratora</Localized>} disabled={mode === 'read'} />
                    </If>

                    <If condition={mode === 'adminEdit'}>
                        <FormControl>
                            <InputLabel id="select-status-label">Status</InputLabel>
                            <Select
                                fullWidth
                                labelId="select-status-label"
                                value={status}
                                onChange={handleStatusChange}
                                label="Status"
                            >
                                <MenuItem value={'CLOSED'}>
                                    <Localized id="user-requests-closed">_Zamknięty</Localized>
                                </MenuItem>
                                <MenuItem value={'REJECTED'}>
                                    <Localized id="user-requests-rejected">_Odrzucony</Localized>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </If>

                    <If condition={mode === 'adminEdit' || mode === 'delete'}>
                        <TextField
                            autoFocus
                            fullWidth
                            sx={{ marginTop: 2 }}
                            InputLabelProps={{ shrink: true }}
                            type="password"
                            value={password}
                            onChange={handlePasswordChange}
                            label={<Localized id="password"><span>Hasło</span></Localized>}
                            helperText={<Localized id="autorization-password-required"><span>Wymagana autoryzacja hasłem</span></Localized>}
                            autoComplete="off"
                            aria-required="true"
                        />
                    </If>
                </Stack>
                {
                    serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                {mode !== "read" && <SaveButton onClick={handleSaveClick} loading={saving} />}
                <CancelButton onClick={onCancel} loading={saving} />
            </DialogActions>
        </Dialog>
    </>
}

